import { useMemo, useState } from "react";
import { Download } from "lucide-react";

import {
  ReportsOverview,
  ReportsAnalytics,
  ReportsRankings,
} from "./PackkyReportsComponents";

// ============================================================
// COMMON HELPERS
// ============================================================

const PIPELINE_STATUSES = [
  "acknowledged",
  "arrived",
  "food is ready",
  "assigned",
  "unassigned",
];

const percentage = (value, total, digits = 1) =>
  total ? ((value / total) * 100).toFixed(digits) : (0).toFixed(digits);

function sortReportRows(rows, sortBy, nameKey) {
  const result = [...rows];

  switch (sortBy) {
    case "tasks_asc":
      return result.sort((a, b) => a.taskCount - b.taskCount);

    case "name_asc":
      return result.sort((a, b) => a[nameKey].localeCompare(b[nameKey]));

    case "name_desc":
      return result.sort((a, b) => b[nameKey].localeCompare(a[nameKey]));

    case "tasks_desc":
    default:
      return result.sort((a, b) => b.taskCount - a.taskCount);
  }
}

// ============================================================
// MAIN REPORTS TAB
// ============================================================

export default function PackkyReportsTab({
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  restaurantData,
  driverTaskAnalytics,
  taskStatusAnalytics,
  onApplyFilter,
  onExport,
}) {
  // ==========================================================
  // LOCAL SEARCH & SORT STATES
  // ==========================================================

  const [shopSearch, setShopSearch] = useState("");
  const [driverSearch, setDriverSearch] = useState("");

  const [shopSortBy, setShopSortBy] = useState("tasks_desc");
  const [driverSortBy, setDriverSortBy] = useState("tasks_desc");

  // ==========================================================
  // NORMALIZE API DATA
  // ==========================================================

  const shops = useMemo(
    () =>
      (Array.isArray(restaurantData) ? restaurantData : []).map((item) => ({
        ...item,
        shopName: String(item?.shopName ?? "").trim() || "Unknown Shop",
        shopCode: String(item?.shopCode || "—"),
        taskCount: Number(item?.taskCount || 0),
      })),
    [restaurantData],
  );

  const drivers = useMemo(
    () =>
      (Array.isArray(driverTaskAnalytics) ? driverTaskAnalytics : []).map(
        (item) => ({
          ...item,
          driverName: String(item?.driverName ?? "").trim() || "Unknown Driver",
          driverCode: String(item?.driverCode || "—"),
          driverMobile: String(item?.driverMobile || "—"),
          taskCount: Number(item?.taskCount || 0),
        }),
      ),
    [driverTaskAnalytics],
  );

  const statuses = useMemo(
    () =>
      (Array.isArray(taskStatusAnalytics) ? taskStatusAnalytics : []).map(
        (item) => ({
          ...item,
          taskStatus: String(item?.taskStatus ?? "").trim() || "unknown",
          taskCount: Number(item?.taskCount || 0),
        }),
      ),
    [taskStatusAnalytics],
  );

  // ==========================================================
  // ALL REPORT ANALYTICS
  // ==========================================================

  const stats = useMemo(() => {
    const totalRestaurantTasks = shops.reduce(
      (sum, item) => sum + item.taskCount,
      0,
    );

    const totalDriverTasks = drivers.reduce(
      (sum, item) => sum + item.taskCount,
      0,
    );

    const totalStatusTasks = statuses.reduce(
      (sum, item) => sum + item.taskCount,
      0,
    );

    const successfulTasks =
      statuses.find((item) => item.taskStatus.toLowerCase() === "successful")
        ?.taskCount || 0;

    const cancelledTasks =
      statuses.find((item) => item.taskStatus.toLowerCase() === "cancelled")
        ?.taskCount || 0;

    const activePipelineTasks = statuses
      .filter((item) =>
        PIPELINE_STATUSES.includes(item.taskStatus.toLowerCase()),
      )
      .reduce((sum, item) => sum + item.taskCount, 0);

    const topShops = [...shops]
      .sort((a, b) => b.taskCount - a.taskCount)
      .slice(0, 5);

    const topDrivers = [...drivers]
      .sort((a, b) => b.taskCount - a.taskCount)
      .slice(0, 5);

    const busiestShop = topShops[0];
    const secondBusiestShop = topShops[1];

    const top2ShopShare = percentage(
      (busiestShop?.taskCount || 0) + (secondBusiestShop?.taskCount || 0),
      totalRestaurantTasks,
    );

    const avgTasksPerShop = shops.length
      ? Math.round(totalRestaurantTasks / shops.length)
      : 0;

    const avgTasksPerDriver = drivers.length
      ? Math.round(totalDriverTasks / drivers.length)
      : 0;

    const unknownShopCount = shops.filter(
      (item) => item.shopName === "Unknown Shop" || item.shopCode === "—",
    ).length;

    const unknownDriverCount = drivers.filter(
      (item) =>
        item.driverName === "Unknown Driver" ||
        item.driverCode === "—" ||
        item.driverMobile === "—",
    ).length;

    const lowestPerformerDriver = [...drivers]
      .filter((item) => item.taskCount > 0)
      .sort((a, b) => a.taskCount - b.taskCount)[0];

    return {
      totalRestaurantTasks,
      totalDriverTasks,
      totalStatusTasks,

      successfulTasks,
      cancelledTasks,
      activePipelineTasks,

      successRate: percentage(successfulTasks, totalStatusTasks, 2),
      cancelRate: percentage(cancelledTasks, totalStatusTasks, 2),

      topShops,
      topDrivers,

      busiestShop,
      secondBusiestShop,
      top2ShopShare,

      avgTasksPerShop,
      avgTasksPerDriver,

      unknownShopCount,
      unknownDriverCount,

      topPerformerDriver: topDrivers[0],
      lowestPerformerDriver,

      shopsCount: shops.length,
      driversCount: drivers.length,
    };
  }, [shops, drivers, statuses]);

  // ==========================================================
  // SHOP SEARCH + SORT
  // ==========================================================

  const filteredShopRows = useMemo(() => {
    const query = shopSearch.trim().toLowerCase();

    const filtered = shops.filter((shop) => {
      if (!query) return true;

      return (
        shop.shopName.toLowerCase().includes(query) ||
        shop.shopCode.toLowerCase().includes(query)
      );
    });

    return sortReportRows(filtered, shopSortBy, "shopName");
  }, [shops, shopSearch, shopSortBy]);

  // ==========================================================
  // DRIVER SEARCH + SORT
  // ==========================================================

  const filteredDriverRows = useMemo(() => {
    const query = driverSearch.trim().toLowerCase();

    const filtered = drivers.filter((driver) => {
      if (!query) return true;

      return (
        driver.driverName.toLowerCase().includes(query) ||
        driver.driverCode.toLowerCase().includes(query) ||
        driver.driverMobile.toLowerCase().includes(query)
      );
    });

    return sortReportRows(filtered, driverSortBy, "driverName");
  }, [drivers, driverSearch, driverSortBy]);

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <>
      {/* DATE FILTERS & EXPORT */}
      <div className="mb-4 rounded-2xl border bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              From Date
            </label>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-11 w-full rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              To Date
            </label>

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-11 w-full rounded-xl border px-3 text-sm outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>

          <button
            type="button"
            onClick={onApplyFilter}
            className="h-11 rounded-xl bg-blue-600 text-sm font-medium text-white hover:bg-blue-700"
          >
            Apply Filter
          </button>

          <button
            type="button"
            onClick={onExport}
            className="flex h-11 items-center justify-center gap-2 rounded-xl bg-green-600 text-sm font-medium text-white hover:bg-green-700"
          >
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI CARDS / HERO / ATTENTION / BADGES */}
      <ReportsOverview stats={stats} />

      {/* STATUS BREAKDOWN / TOP SHOPS / TOP DRIVERS / CHARTS */}
      <ReportsAnalytics stats={stats} statuses={statuses} />

      {/* SEARCHABLE & SORTABLE TABLES */}
      <ReportsRankings
        shops={filteredShopRows}
        drivers={filteredDriverRows}
        shopsCount={shops.length}
        driversCount={drivers.length}
        totalRestaurantTasks={stats.totalRestaurantTasks}
        shopSearch={shopSearch}
        setShopSearch={setShopSearch}
        driverSearch={driverSearch}
        setDriverSearch={setDriverSearch}
        shopSortBy={shopSortBy}
        setShopSortBy={setShopSortBy}
        driverSortBy={driverSortBy}
        setDriverSortBy={setDriverSortBy}
      />
    </>
  );
}
